import { format } from 'date-fns';
import { Icon as GddsIcon, Typo, Col } from '@geberit/gdds';

// styles
import styles from './tile.module.scss';

// types
import { type TilesProps, type Download, mapFileToIcon } from '../../types';

// components
import { Translation } from 'components/Translation/Translation';
import { Image as StaticImage } from 'components/image/image';
import { InnerHtml } from 'components/InnerHtml/inner-html';

// utils
import { useTracking } from 'utils/hooks/useTracking';
import {
  clickAddToOrderCart,
  clickAddToDownloadCart,
  clickDownloadsLink,
} from 'features/download-center/tracking';
import { formatBytes } from 'utils/format-bytes';
import { useLocale } from 'utils/use-locale';
import { useCurrentLanguage } from 'utils/hooks/useCurrentLanguage';
import { useKolo } from 'utils/hooks/use-kolo';
import { isEmpty } from 'utils/is-empty';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useTracking as useTracking2 } from 'utils/tracking/track';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { getFullPath } from '../../../helpers';
import { decodingContent } from 'utils/decodingContent';

export default function Tile(props: Readonly<Omit<TilesProps, 'data'> & { tileData: Download }>) {
  const { dateFormat, updateOrder, updateDownload, downloads, tileData: data, hasOrder } = props;
  const isKolo = useKolo();
  const track = useTracking();
  const { trackClick, trackEvent } = useTracking2();
  const translate = useTranslationFunction();

  const {
    size: fileSize,
    date: dateString,
    // eslint-disable-next-line camelcase
    meta: {
      title,
      fileUrl,
      flipbookUrl,
      indexer_filetype,
      picture,
      order: showOrder,
      fileExtension,
    },
  } = data;

  const language = useCurrentLanguage();
  const locale = useLocale(language);
  const formatedDateString = locale
    ? format(new Date(dateString), dateFormat, {
        locale,
      })
    : '';

  const hasDownload = downloads.some((download) => download.id === data.id);

  const handleClickOrder = () => {
    // add tile product with default amount 1 to order section in cart
    const order = { ...data, amount: 1, title: title[0] };
    updateOrder(order);

    track.trackEvent(clickAddToOrderCart(title[0]));

    let clickIntent = 'add to order - download center';

    if (hasOrder) {
      clickIntent = 'remove from order - download center';
    }

    trackEvent({
      event: 'file_download',
      click_intent: clickIntent,
      click_element: 'download center',
      file_name: decodingContent(title?.[0]),
      value: 5,
    });
  };

  const handleClickDownload = () => {
    // add tile product to download section in cart
    updateDownload(data);

    track.trackEvent(clickAddToDownloadCart(data.title));

    let clickIntent = 'add to basket - download center';

    if (hasDownload) {
      clickIntent = 'remove from basket - download center';
    }

    trackEvent({
      event: 'file_download',
      click_intent: clickIntent,
      click_element: 'download center',
      file_name: decodingContent(title?.[0]),
      value: 5,
    });
  };

  const handleDownloadsLinkClick = (name: 'Open' | 'Flipbook', fileUrl = '', fileType = 'file') => {
    track.trackEvent(clickDownloadsLink(name, data.title));
    if (name === 'Open')
      trackEvent({
        event: 'file_download',
        click_intent: `open ${fileType} - download center`,
        click_url: getFullPath(fileUrl) ?? fileUrl,
        file_name: decodingContent(title?.[0]),
        file_extension: fileType,
        value: 5,
      });
  };

  const checkBooleanInArray = (array) => array.some((string) => string !== 'false');

  return (
    <Col size={[4, 4, 4]}>
      <div className={styles.tile}>
        <div className={styles.badges}>
          {hasDownload && (
            <span>
              <GddsIcon symbol="Download" color="#FFFFFF" />
            </span>
          )}
          {hasOrder && (
            <span>
              <GddsIcon symbol="Onlineshop" color="#FFFFFF" />
            </span>
          )}
        </div>
        <div className={styles.tileContent}>
          {!isEmpty(picture) && !isEmpty(picture[0]) && (
            <StaticImage
              src={picture[0]}
              alt={title[0]}
              title={title[0]}
              staticImage
              external
              className={styles.image}
            />
          )}
          <div className={styles.information}>
            {!isEmpty(dateString) && dateString !== '1970-01-01T00:00:00Z' && (
              <Typo variant="p2" as="span" className={styles.date}>
                {formatedDateString}
              </Typo>
            )}
            {!isEmpty(title) && !isEmpty(title[0]) && (
              <Typo
                variant="p1"
                as="span"
                className={classNameBuilder(styles.title, isKolo && styles.titleKolo)}
              >
                <InnerHtml as="span" content={title[0]} />
              </Typo>
            )}
            <div className={styles.flags}>
              {!isEmpty(indexer_filetype) && !isEmpty(indexer_filetype[0]) && (
                <Typo variant="p2" as="span">
                  <GddsIcon
                    symbol={
                      fileExtension?.[0] && mapFileToIcon[fileExtension?.[0]]
                        ? mapFileToIcon[fileExtension?.[0]]
                        : 'document'
                    }
                  />
                  <div className={styles.filename}>{String(indexer_filetype[0]).toUpperCase()}</div>
                </Typo>
              )}
              {!isEmpty(fileUrl) && !isEmpty(fileUrl[0]) && (
                <Typo variant="p2" as="span">
                  {'(' + formatBytes(fileSize * 1000, 2) + ')'}
                </Typo>
              )}
            </div>
          </div>
        </div>
        <div className={styles.overlay}>
          <div className={styles.overlayContent}>
            {!isEmpty(fileUrl) && !isEmpty(fileUrl[0]) && (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={fileUrl[0]}
                onClick={() =>
                  handleDownloadsLinkClick(
                    'Open',
                    fileUrl[0],
                    fileExtension?.[0] ?? indexer_filetype?.[0],
                  )
                }
              >
                <GddsIcon
                  symbol={
                    fileExtension?.[0] && mapFileToIcon[fileExtension[0]]
                      ? mapFileToIcon[fileExtension?.[0]]
                      : 'document'
                  }
                />
                {translate('web20_downloadcenter_open')}
              </a>
            )}
            {!isEmpty(fileUrl) && !isEmpty(fileUrl[0]) && (
              <button
                type="button"
                onClick={handleClickDownload}
                className={classNameBuilder(hasDownload && styles.selected)}
              >
                <GddsIcon symbol="Download" />
                {translate('web20_downloadcenter_download')}
              </button>
            )}
            {!isEmpty(showOrder) && checkBooleanInArray(showOrder) && (
              <button
                type="button"
                onClick={handleClickOrder}
                className={classNameBuilder(hasOrder && styles.selected)}
              >
                <GddsIcon symbol="Onlineshop" />
                {translate('web20_downloadcenter_order')}
              </button>
            )}
            {!isEmpty(flipbookUrl) && !isEmpty(flipbookUrl[0]) && (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={flipbookUrl[0]}
                onClick={() => handleDownloadsLinkClick('Flipbook')}
              >
                <GddsIcon symbol="catalogue" />
                <Translation id="web20_downloadcenter_flipbook" />
              </a>
            )}
          </div>
        </div>
      </div>
    </Col>
  );
}
